import { useAccount } from '@azure/msal-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Dropdown, DropdownOption, Link, RadioButton, TextField, Typography } from '@volvo/vcdk-react';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useForm, type Control, type SubmitHandler } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import type { ITelemetryService } from '@/service/vpc/types/ITelemetryService';

import Checkbox from '@/components/Checkbox';
import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import Modal from '@/components/modals/AppModal';
import { useServiceProvisioning } from '@/hooks/useUnitServices';
import { getEndCustomerQueryOptions, queryKeys } from '@/service/vpc/queryOptions';
import { legalContractDocumentUrl } from '@/service/vpc/vpcService';
import { retrieveAccountHolderName, retrieveAccountName, retrieveEmail } from '@/utils/account';
import { dealerLocatorLink } from '@/utils/baseLinks';
import { countries } from '@/utils/country';
import { getActivationThanksConfig } from '@/utils/service-activation';
import { marketForCountry, retrieveCountry } from '@/utils/services';

const StepEnum = {
  ACTIVATE_SERVICE: 'ACTIVATE_SERVICE',
  CONTRACT_DETAILS: 'CONTRACT_DETAILS',
  CHOOSE_RECEIVER: 'CHOOSE_RECEIVER',
  THANKS: 'THANKS',
  DEACTIVATE_SERVICE: 'DEACTIVATE_SERVICE',
} as const;

type StepEnum = (typeof StepEnum)[keyof typeof StepEnum];

// Whether the activation is on behalf of the signed-in individual or a company.
type ActivationBehalf = 'myself' | 'company';

export type IServicesFormData = {
  selectedServices: ITelemetryService[];
  registeredOwner: string;
  authorizedSignatory: string;
  companyName: string;
  vatNumber: string;
  country: string;
  address: string;
  city: string;
  stateProvince?: string;
  zipCode: string;
  dealer: string;
  isCorrectInfo: boolean;
  isTermsChecked: boolean;
  isServiceDetailsChecked: boolean;
  isDataActChecked: boolean;
};

// Form-only contact fields (AB#1570293): shown in Contact details, stripped before the wire
// payload — the backend contract stays registeredOwner/authorizedSignatory strings, no email.
type ServicesFormFields = IServicesFormData & {
  firstName: string;
  lastName: string;
  email: string;
};

type ModalProps = {
  endCustomerUnitId: string;
  segment: 'Marine' | 'Industrial';
  services: ITelemetryService[];
  deactivationServices: ITelemetryService[];
};

export type DigitalServiceModalHandle = {
  openActivate: () => void;
  openTerminate: () => void;
};

const DigitalServiceModal = forwardRef<DigitalServiceModalHandle, ModalProps>(
  ({ endCustomerUnitId, segment, services, deactivationServices }, ref) => {
    const account = useAccount();
    const queryClient = useQueryClient();
    const { data: endCustomer } = useQuery(getEndCustomerQueryOptions());
    const { activateServices, isPending: isActivating } = useServiceProvisioning(segment);

    // Only Marine offers the Myself/Company choice; Industrial activations start at contract
    // details and follow the company flow (ContractDetailsContent defaults behalf to 'company').
    const activateEntryStep = segment === 'Marine' ? StepEnum.ACTIVATE_SERVICE : StepEnum.CONTRACT_DETAILS;

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [currentStep, setCurrentStep] = useState<StepEnum>(activateEntryStep);
    const [behalf, setBehalf] = useState<ActivationBehalf | null>(null);

    const isActive = services.every((service) => service.status === 'Active' || service.status === 'Requested');
    const validServices = services.filter((service) => service.canActivate || service.dependencyInfo.canBatchActivate);

    const { handleSubmit, control, formState, setValue, getValues } = useForm<ServicesFormFields>({
      defaultValues: {
        selectedServices: validServices,
        registeredOwner: account ? retrieveAccountName(account) : '',
        authorizedSignatory: account ? retrieveAccountName(account) : '',
        firstName: account ? retrieveAccountHolderName(account).firstName : '',
        lastName: account ? retrieveAccountHolderName(account).surName : '',
        email: account ? retrieveEmail(account) : '',
        companyName: '',
        vatNumber: '',
        address: '',
        zipCode: '',
        city: '',
        stateProvince: '',
        country: '',
        dealer: '',
        isCorrectInfo: false,
        isTermsChecked: false,
        isServiceDetailsChecked: false,
        isDataActChecked: false,
      },
    });

    // Pre-populate contract details from the ECM end-customer record (AB#1427029). Only fills
    // fields that are still empty so neither token-prefilled claims nor user input get overwritten.
    useEffect(() => {
      if (!endCustomer) return;
      const fields: Array<[keyof ServicesFormFields, string | undefined]> = [
        ['firstName', endCustomer.firstName],
        ['lastName', endCustomer.surname],
        ['email', endCustomer.email],
        ['companyName', endCustomer.companyName],
        ['vatNumber', endCustomer.vatNumber],
        ['address', [endCustomer.addressFirstPart, endCustomer.addressSecondPart].filter(Boolean).join(', ')],
        ['zipCode', endCustomer.postalCode],
        ['city', endCustomer.city],
        ['stateProvince', endCustomer.county],
        ['country', countries.find((c) => c.code === endCustomer.shortCountryCode?.toUpperCase())?.name],
      ];
      for (const [name, value] of fields) {
        const current = getValues(name);
        if (value?.trim() && !(typeof current === 'string' && current.trim())) {
          setValue(name, value, { shouldValidate: true });
        }
      }
    }, [endCustomer, getValues, setValue]);

    // A VAT number on record is authoritative; the user only types one when ECM has none.
    const vatLocked = !!endCustomer?.vatNumber?.trim();

    useImperativeHandle(
      ref,
      () => ({
        openActivate: () => {
          setBehalf(null);
          // Re-sync the form's service selection on open — defaultValues only capture the
          // mount-time services, and the activatable subset changes as query data refreshes
          // (e.g. "Activate all" clicked after another service was activated via its card).
          setValue('selectedServices', validServices);
          setCurrentStep(activateEntryStep);
          setIsOpen(true);
        },
        openTerminate: () => {
          setCurrentStep(StepEnum.DEACTIVATE_SERVICE);
          setIsOpen(true);
        },
      }),
      [activateEntryStep, setValue, validServices]
    );

    const onSubmit: SubmitHandler<ServicesFormFields> = async (formData) => {
      if (!endCustomerUnitId) return;
      // Strip the form-only contact fields; the wire payload keeps its existing shape with the
      // composed full name (email is display-only until the backend accepts it).
      const { firstName, lastName, email, ...payload } = formData;
      void email;
      const fullName = `${firstName} ${lastName}`.trim();
      payload.registeredOwner = fullName;
      payload.authorizedSignatory = fullName;
      const serviceIds = payload.selectedServices.map((s) => s.serviceId || 0);
      const success = await activateServices(endCustomerUnitId, serviceIds, payload);
      if (success) {
        const thanksConfig = getActivationThanksConfig(formData.selectedServices);
        if (thanksConfig.show) {
          setCurrentStep(StepEnum.THANKS);
        }
      }
    };

    const close = () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.getServices], exact: false });
      queryClient.invalidateQueries({ queryKey: [queryKeys.getUnitsWithFBB] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.getUnitsLastSeen] });
      setIsOpen(false);
      setBehalf(null);
      setCurrentStep(isActive ? activateEntryStep : StepEnum.DEACTIVATE_SERVICE);
    };

    // Step handler
    const renderStep = () => {
      switch (currentStep) {
        case StepEnum.ACTIVATE_SERVICE:
          return (
            <ActivateServiceContent
              behalf={behalf}
              onSelect={setBehalf}
              onCancel={close}
              onNext={() => {
                // The company-name field is unmounted for the "myself" path; the backend rejects an
                // empty companyName, so send the sentinel "none" to pass its required-field validation.
                if (behalf === 'myself') {
                  setValue('companyName', 'none');
                } else if (getValues('companyName') === 'none') {
                  // Don't leak the sentinel into the visible Company name field after a cancelled "myself" run.
                  setValue('companyName', endCustomer?.companyName ?? '');
                }
                setCurrentStep(StepEnum.CONTRACT_DETAILS);
              }}
            />
          );
        case StepEnum.CONTRACT_DETAILS:
          return (
            <ContractDetailsContent
              behalf={behalf ?? 'company'}
              segment={segment || 'Unknown'}
              control={control}
              services={validServices}
              vatLocked={vatLocked}
              isValid={formState.isValid}
              isPending={isActivating}
              onCancel={close}
              onNext={async () => {
                const needsDealerStep = validServices.some(
                  (service) =>
                    service.serviceName === 'ActiveMonitoring' || service.serviceName === 'ActiveMonitoringEcWifi'
                );
                if (needsDealerStep) {
                  setCurrentStep(StepEnum.CHOOSE_RECEIVER);
                } else {
                  await handleSubmit(onSubmit)();
                }
              }}
            />
          );
        case StepEnum.CHOOSE_RECEIVER:
          return (
            <ChooseReceiverContent
              control={control}
              isValid={formState.isValid}
              isPending={isActivating}
              onPrevious={() => setCurrentStep(StepEnum.CONTRACT_DETAILS)}
              onNext={async () => {
                await handleSubmit(onSubmit)();
              }}
            />
          );
        case StepEnum.THANKS:
          return (
            <ThanksContent
              segment={segment}
              services={validServices}
              onClose={() => {
                close();
              }}
            />
          );
        case StepEnum.DEACTIVATE_SERVICE:
          return (
            <DeactivateServiceContent
              segment={segment}
              endCustomerUnitId={endCustomerUnitId}
              services={deactivationServices}
              onClose={close}
            />
          );
        default:
          return <></>;
      }
    };

    return (
      <>
        {isOpen &&
          createPortal(
            <form onSubmit={(e) => e.preventDefault()}>
              <Modal open={isOpen} onClose={close} maxWidth="90%" width={700}>
                {renderStep()}
              </Modal>
            </form>,
            document.body
          )}
      </>
    );
  }
);

DigitalServiceModal.displayName = 'DigitalServiceModal';

type ActivateServiceContentProps = {
  behalf: ActivationBehalf | null;
  onSelect: (behalf: ActivationBehalf) => void;
  onCancel: () => void;
  onNext: () => void;
};

const ActivateServiceContent: React.FC<ActivateServiceContentProps> = ({ behalf, onSelect, onCancel, onNext }) => {
  const { t } = useTranslation();

  return (
    <>
      <span slot="header">{t(($) => $.components.modal.digital_services_modal.activate_service_content.title)}</span>
      <div className="mb-4 flex flex-col gap-6">
        <Typography>
          {t(($) => $.components.modal.digital_services_modal.activate_service_content.description)}
        </Typography>
        <div className="flex flex-col gap-4">
          <RadioButton
            name="activationBehalf"
            value="myself"
            label={t(($) => $.components.modal.digital_services_modal.activate_service_content.myself)}
            helperText={t(($) => $.components.modal.digital_services_modal.activate_service_content.myself_helper)}
            checked={behalf === 'myself'}
            onChange={(e) => e.detail.checked && onSelect('myself')}
          />
          <RadioButton
            name="activationBehalf"
            value="company"
            label={t(($) => $.components.modal.digital_services_modal.activate_service_content.company)}
            helperText={t(($) => $.components.modal.digital_services_modal.activate_service_content.company_helper)}
            checked={behalf === 'company'}
            onChange={(e) => e.detail.checked && onSelect('company')}
          />
        </div>
      </div>
      <span slot="modalFooter">
        <div className="flex gap-4">
          <Button variant="secondary" onClick={onCancel}>
            {t(($) => $.common.cancel)}
          </Button>
          <Button variant="primary" onClick={onNext} disabled={behalf === null}>
            {t(($) => $.common.continue)}
          </Button>
        </div>
      </span>
    </>
  );
};

type ContractDetailsContentProps = {
  behalf: ActivationBehalf;
  services: ITelemetryService[];
  segment: 'Marine' | 'Industrial';
  control: Control<ServicesFormFields, unknown, ServicesFormFields>;
  vatLocked: boolean;
  isValid: boolean;
  isPending: boolean;
  onCancel: () => void;
  onNext: () => void;
};

const ContractDetailsContent: React.FC<ContractDetailsContentProps> = ({
  behalf,
  services,
  segment,
  control,
  vatLocked,
  isValid,
  isPending,
  onCancel,
  onNext,
}) => {
  const { t } = useTranslation();
  const account = useAccount();

  // All services in one modal share the same segment + market, so any service's offer serves
  // the shared T&C / Data Management Agreement links.
  const offer = services[0]?.serviceOffer;
  const termsContract = offer?.legalContracts.find((c) => c.ssmContractType.startsWith('TERMS_AND_CONDITIONS'));
  const dmaContract = offer?.legalContracts.find((c) => c.ssmContractType.startsWith('DATA_MANAGEMENT_AGREEMENT'));

  // EU Data Act consent only applies to products placed on an EU market (AB#1540471).
  // In-flight rows carry no serviceOffer (backend keeps the subscription's clone row
  // un-market-matched), so check any offer in the batch, then the token country claim —
  // the same signal the backend resolves the market from. Unresolvable market keeps it.
  const userMarket =
    services.find((s) => s.serviceOffer)?.serviceOffer?.market ?? marketForCountry(retrieveCountry(account));
  const showDataAct = !userMarket || userMarket === 'EU';

  // Token-prefilled contact fields are locked; only claims the token is missing stay editable.
  // Computed from the account once, not from form values — typing must never lock a field.
  const prefilled = useMemo(() => {
    const holderName = account ? retrieveAccountHolderName(account) : undefined;
    return {
      firstName: !!holderName?.firstName,
      lastName: !!holderName?.surName,
      email: !!(account && retrieveEmail(account)),
    };
  }, [account]);

  const serviceDescriptions = useMemo<Array<{ key: string; label: string; href: string }>>(
    () =>
      services.flatMap<{ key: string; label: string; href: string }>((service) => {
        // Market-matched backend: one link per SERVICE_DESCRIPTION contract (industrial
        // ActiveMonitoring carries two), opened via the anonymous document route so the
        // document name stays in the browser URL (same as service card Read more).
        const contracts = (service.serviceOffer?.legalContracts ?? []).filter((c) =>
          c.ssmContractType.includes('SERVICE_DESCRIPTION')
        );
        if (contracts.length > 0) {
          return contracts.map((contract) => ({
            key: `${service.serviceName}-${contract.id}`,
            label: contract.contractDisplayName,
            href: legalContractDocumentUrl(contract.uri),
          }));
        }

        // Fallback (no serviceOffer / unmatched market): today's static hrefs + labels.
        const fallback: Array<{ key: string; label: string; href: string }> = [
          {
            key: service.serviceName,
            label: t(
              ($) => $.components.modal.digital_services_modal.contract_details_content.service_description_for,
              {
                serviceName: t(
                  ($) =>
                    $.pages.unit.services.list[service.serviceName as keyof typeof $.pages.unit.services.list][segment]
                      .title
                ),
              }
            ),
            href: `${import.meta.env.VITE_BASE_PATH}documents/${
              service.serviceName === 'ActiveMonitoring'
                ? segment === 'Marine'
                  ? 'Service-Description-Connected-Services-Extended'
                  : 'Service-Description-Active-Monitoring'
                : service.serviceName === 'ActiveMonitoringCore'
                  ? 'Service-Description-Connected-Services-Core'
                  : service.serviceName === 'ActiveMonitoringEcWifi'
                    ? 'Service-Description-Connected-Services-Extended'
                    : 'Service-Description-Location-Services'
            }.pdf`,
          },
        ];
        // Industrial ActiveMonitoring bundles a second static Remote Software Updates description.
        if (service.serviceName === 'ActiveMonitoring' && segment === 'Industrial') {
          fallback.push({
            key: 'Remote_Software_Update',
            label: t(
              ($) => $.components.modal.digital_services_modal.contract_details_content.service_description_for,
              {
                serviceName: t(
                  ($) =>
                    $.components.modal.digital_services_modal.contract_details_content.remote_software_updates_title
                ),
              }
            ),
            href: `${import.meta.env.VITE_BASE_PATH}documents/Service-Description-Remote-Software-Updates.pdf`,
          });
        }
        return fallback;
      }),
    [services, segment, t]
  );

  const serviceDescriptionLinks = useMemo(
    () => (
      <>
        {serviceDescriptions.map((description, index) => {
          const separator = index < serviceDescriptions.length - 1 ? ',' : null;
          return (
            <React.Fragment key={description.key}>
              <Link variant="inline-discreet" href={description.href} target="_blank" rel="noopener noreferrer">
                {description.label}
                {separator}
              </Link>
              {index < serviceDescriptions.length - 1 ? ' ' : null}
            </React.Fragment>
          );
        })}
      </>
    ),
    [serviceDescriptions]
  );

  return (
    <>
      <span slot="header">{t(($) => $.components.modal.digital_services_modal.contract_details_content.title)}</span>
      <div className="mb-4 flex flex-col gap-8">
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Controller
              name="firstName"
              control={control}
              rules={{
                required: true,
                validate: (value) => value.trim().length > 0,
              }}
              render={({ field }) => (
                <TextField
                  required
                  disabled={prefilled.firstName}
                  size="medium"
                  label={t(($) => $.components.modal.digital_services_modal.contract_details_content.first_name)}
                  className="w-full"
                  value={field.value}
                  onInput={(e) => field.onChange(e.detail.value)}
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              rules={{
                required: true,
                validate: (value) => value.trim().length > 0,
              }}
              render={({ field }) => (
                <TextField
                  required
                  disabled={prefilled.lastName}
                  size="medium"
                  label={t(($) => $.components.modal.digital_services_modal.contract_details_content.last_name)}
                  className="w-full"
                  value={field.value}
                  onInput={(e) => field.onChange(e.detail.value)}
                />
              )}
            />
          </div>
          <Controller
            name="email"
            control={control}
            rules={{
              required: true,
              validate: (value) => /^\S+@\S+\.\S+$/.test(value.trim()),
            }}
            render={({ field }) => (
              <TextField
                required
                disabled={prefilled.email}
                size="medium"
                label={t(($) => $.components.modal.digital_services_modal.contract_details_content.email_address)}
                className="w-full"
                value={field.value}
                onInput={(e) => field.onChange(e.detail.value)}
              />
            )}
          />
          {behalf === 'company' && (
            <>
              <div className="flex flex-col gap-2">
                <Typography variant="subtitle2">
                  {t(($) => $.components.modal.digital_services_modal.contract_details_content.company_details_title)}
                </Typography>
                <Typography>
                  {t(
                    ($) =>
                      $.components.modal.digital_services_modal.contract_details_content.company_details_description
                  )}
                </Typography>
              </div>
              <Controller
                name="companyName"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => value.trim().length > 0,
                }}
                render={({ field }) => (
                  <TextField
                    required
                    size="medium"
                    label={t(($) => $.components.modal.digital_services_modal.contract_details_content.company_name)}
                    className="w-full"
                    value={field.value}
                    onInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
            </>
          )}
          <Controller
            name="vatNumber"
            control={control}
            rules={{
              required: true,
              validate: (value) => value.trim().length > 0,
            }}
            render={({ field }) => (
              <TextField
                required
                disabled={vatLocked}
                size="medium"
                label={t(($) =>
                  behalf === 'company'
                    ? $.components.modal.digital_services_modal.contract_details_content.vat_number
                    : $.components.modal.digital_services_modal.contract_details_content.social_security_number
                )}
                className="w-full"
                value={field.value}
                onInput={(e) => field.onChange(e.detail.value)}
              />
            )}
          />

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Controller
              name="address"
              control={control}
              rules={{
                required: true,
                validate: (value) => value.trim().length > 0,
              }}
              render={({ field }) => (
                <TextField
                  required
                  size="medium"
                  label={t(($) => $.components.modal.digital_services_modal.contract_details_content.address)}
                  className="w-full sm:col-span-2"
                  value={field.value}
                  onInput={(e) => field.onChange(e.detail.value)}
                />
              )}
            />
            <Controller
              name="zipCode"
              control={control}
              rules={{
                required: true,
                validate: (value) => value.trim().length > 0,
              }}
              render={({ field }) => (
                <TextField
                  required
                  size="medium"
                  label={t(($) => $.components.modal.digital_services_modal.contract_details_content.postal_code)}
                  className="w-full"
                  value={field.value}
                  onInput={(e) => field.onChange(e.detail.value)}
                />
              )}
            />
            <Controller
              name="city"
              control={control}
              rules={{
                required: true,
                validate: (value) => value.trim().length > 0,
              }}
              render={({ field }) => (
                <TextField
                  required
                  size="medium"
                  label={t(($) => $.components.modal.digital_services_modal.contract_details_content.city)}
                  className="w-full"
                  value={field.value}
                  onInput={(e) => field.onChange(e.detail.value)}
                />
              )}
            />
            <Controller
              name="stateProvince"
              control={control}
              render={({ field }) => (
                <TextField
                  size="medium"
                  label={t(($) => $.components.modal.digital_services_modal.contract_details_content.state_province)}
                  className="w-full"
                  value={field.value ?? ''}
                  onInput={(e) => field.onChange(e.detail.value)}
                />
              )}
            />
            <Controller
              name="country"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <Dropdown
                  required
                  size="medium"
                  label={t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.country)}
                  filterInput
                  className="w-full [&::part(input-wrapper)]:h-10"
                  floatingLabel={false}
                  multi={false}
                  disableCloseOnSelect
                  value={field.value}
                  onChange={(e) => field.onChange(e.detail.value)}
                >
                  {countries.map((country) => (
                    <DropdownOption key={country.code} value={country.name}>
                      {country.name}
                    </DropdownOption>
                  ))}
                </Dropdown>
              )}
            />
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <Controller
            name="isCorrectInfo"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field }) => (
              <Checkbox
                required
                label={t(($) => $.components.modal.digital_services_modal.contract_details_content.is_correct_info)}
                onChange={(e) => field.onChange(e.detail.checked)}
                checked={field.value}
              />
            )}
          />
          <Controller
            name="isTermsChecked"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field }) => (
              <Checkbox
                required
                label={
                  <>
                    <Trans
                      i18nKey={($) =>
                        $.components.modal.digital_services_modal.contract_details_content.is_terms_checked
                      }
                      components={{
                        a: termsContract ? (
                          <Link
                            variant="inline-discreet"
                            href={legalContractDocumentUrl(termsContract.uri)}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ) : (
                          <Link
                            variant="inline-discreet"
                            href={`${import.meta.env.VITE_BASE_PATH}documents/Digital-Services-Agreement.pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                      }}
                    />
                    {dmaContract && (
                      <>
                        {' '}
                        <Trans
                          i18nKey={($) =>
                            $.components.modal.digital_services_modal.contract_details_content.data_management_agreement
                          }
                          components={{
                            a: (
                              <Link
                                variant="inline-discreet"
                                href={legalContractDocumentUrl(dmaContract.uri)}
                                target="_blank"
                                rel="noopener noreferrer"
                              />
                            ),
                          }}
                        />
                      </>
                    )}
                  </>
                }
                onChange={(e) => field.onChange(e.detail.checked)}
                checked={field.value}
              />
            )}
          />
          <Controller
            name="isServiceDetailsChecked"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field }) => (
              <Checkbox
                required
                label={
                  <Trans
                    i18nKey={($) =>
                      $.components.modal.digital_services_modal.contract_details_content.is_service_details_checked
                    }
                    components={{
                      serviceDescription: serviceDescriptionLinks,
                    }}
                  />
                }
                onChange={(e) => field.onChange(e.detail.checked)}
                checked={field.value}
              />
            )}
          />
          {showDataAct && (
            <Controller
              name="isDataActChecked"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <Checkbox
                  required
                  label={
                    <Trans
                      i18nKey={($) =>
                        $.components.modal.digital_services_modal.contract_details_content.is_data_act_checked
                      }
                      components={{
                        a: (
                          <Link
                            variant="inline-discreet"
                            href="https://www.volvopenta.com/legal/eu-data-act/"
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                      }}
                    />
                  }
                  onChange={(e) => field.onChange(e.detail.checked)}
                  checked={field.value}
                />
              )}
            />
          )}
        </div>
      </div>
      <span slot="modalFooter">
        <div className="flex gap-4">
          <Button variant="secondary" onClick={onCancel}>
            {t(($) => $.common.cancel)}
          </Button>
          <Button type="submit" variant="primary" onClick={onNext} disabled={!isValid || isPending} loading={isPending}>
            {t(($) => $.common.continue)}
          </Button>
        </div>
      </span>
    </>
  );
};

type ChooseReceiverContentProps = {
  control: Control<ServicesFormFields, unknown, ServicesFormFields>;
  isValid: boolean;
  isPending: boolean;
  onPrevious: () => void;
  onNext: () => void;
};

const ChooseReceiverContent: React.FC<ChooseReceiverContentProps> = ({
  control,
  isValid,
  isPending,
  onPrevious,
  onNext,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <span slot="header">{t(($) => $.components.modal.digital_services_modal.choose_receiver_content.title)}</span>
      <div className="flex flex-col gap-8">
        <Typography>
          {t(($) => $.components.modal.digital_services_modal.choose_receiver_content.description)}
        </Typography>
        <Link
          target="_blank"
          rel="noopener noreferrer"
          href={dealerLocatorLink}
          variant="standalone-discreet"
          icon="link-external"
          iconPosition="end"
        >
          {t(($) => $.components.modal.digital_services_modal.choose_receiver_content.find_nearest_dealer)}
        </Link>
        <div className="flex flex-col gap-4">
          <Controller
            name="dealer"
            control={control}
            render={({ field }) => (
              <TextField
                size="medium"
                label={t(($) => $.components.modal.digital_services_modal.choose_receiver_content.dealer)}
                placeholder={t(
                  ($) => $.components.modal.digital_services_modal.choose_receiver_content.dealer_placeholder
                )}
                className="w-full"
                value={field.value}
                onInput={(e) => field.onChange(e.detail.value)}
              />
            )}
          />
        </div>
      </div>
      <span slot="modalFooter">
        <div className="flex gap-4">
          <Button variant="secondary" disabled={isPending} onClick={onPrevious}>
            {t(($) => $.common.back)}
          </Button>
          <Button type="submit" variant="primary" disabled={!isValid || isPending} loading={isPending} onClick={onNext}>
            {t(($) => $.common.continue)}
          </Button>
        </div>
      </span>
    </>
  );
};

type ThanksContentProps = {
  segment: 'Marine' | 'Industrial';
  services: ITelemetryService[];
  onClose: () => void;
};

const ThanksContent: React.FC<ThanksContentProps> = ({ segment, services, onClose }) => {
  const { t } = useTranslation();
  const config = getActivationThanksConfig(services);
  const isMarine = segment === 'Marine';

  return (
    <>
      <span slot="header">{t(($) => $.components.modal.digital_services_modal.thanks_content.title)}</span>
      <Typography>
        {isMarine
          ? t(($) => $.components.modal.digital_services_modal.thanks_content.Marine.description)
          : t(($) => $.components.modal.digital_services_modal.thanks_content[config.descriptionKey])}
      </Typography>
      <span slot="modalFooter">
        <Button variant="primary" onClick={onClose}>
          {t(($) => $.common.close)}
        </Button>
      </span>
    </>
  );
};

type DeactivateServiceContentProps = {
  segment: 'Marine' | 'Industrial';
  endCustomerUnitId: string;
  services: ITelemetryService[];
  onClose: () => void;
};

const DeactivateServiceContent: React.FC<DeactivateServiceContentProps> = ({
  segment,
  endCustomerUnitId,
  services,
  onClose,
}) => {
  const { t } = useTranslation();
  const { deactivateServices, isPending } = useServiceProvisioning(segment);

  const handleDeactivate = async () => {
    try {
      await deactivateServices(endCustomerUnitId, services);
      onClose();
    } catch {
      // Modal stays open on error
    }
  };
  return (
    <>
      <span slot="header">{t(($) => $.components.modal.digital_services_modal.deactivate_service.title)}</span>
      <Typography>
        {services.length > 1
          ? t(($) => $.components.modal.digital_services_modal.deactivate_service.description_all, {
              serviceNames: services
                .map((s) =>
                  t(
                    ($) =>
                      $.pages.unit.services.list[s.serviceName as keyof typeof $.pages.unit.services.list][segment]
                        .title
                  )
                )
                .join(', '),
            })
          : t(($) => $.components.modal.digital_services_modal.deactivate_service.description)}
      </Typography>
      <span slot="modalFooter">
        <div className="flex gap-4">
          <Button variant="primary" disabled={isPending} onClick={onClose}>
            {t(($) => $.common.close)}
          </Button>
          <Button variant="secondary" loading={isPending} disabled={isPending} onClick={() => handleDeactivate()}>
            {t(($) => $.components.modal.digital_services_modal.deactivate_service.deactivate)}
          </Button>
        </div>
      </span>
    </>
  );
};

export default withComponentErrorBoundary(DigitalServiceModal);
